<template>
  <div>
    <div class="carousel" aria-roledescription="carousel" aria-label="Mission Squad connectors">
      <div class="carousel-head">
        <span class="carousel-count text-disabled">{{ connectorCount }} connectors</span>
        <div class="carousel-nav">
          <v-btn
            class="carousel-btn"
            variant="text"
            :ripple="false"
            :disabled="atStart"
            aria-label="Previous connectors"
            @click="prevPage"
          >
            <v-icon icon="mdi-chevron-left" size="22" />
          </v-btn>
          <v-btn
            class="carousel-btn"
            variant="text"
            :ripple="false"
            :disabled="atEnd"
            aria-label="Next connectors"
            @click="nextPage"
          >
            <v-icon icon="mdi-chevron-right" size="22" />
          </v-btn>
        </div>
      </div>

      <div
        ref="viewport"
        class="carousel-viewport"
        tabindex="0"
        role="group"
        aria-label="Connectors — use arrow keys to browse"
        @scroll="onScroll"
        @keydown.right.prevent="nextPage"
        @keydown.left.prevent="prevPage"
      >
        <div ref="track" class="carousel-track">
          <v-card
            v-for="c in connectors"
            :key="c.name"
            class="cx-card"
            :class="{ 'cx-card--byo': c.byo }"
            color="surface"
            rounded="lg"
            :variant="c.byo ? 'outlined' : 'elevated'"
            :elevation="c.byo ? undefined : 1"
          >
            <div v-if="c.byo" class="cx-logo--byo bg-primary">
              <v-icon icon="mdi-plus" size="22" />
            </div>
            <LogoTile v-else :logo="c.logo" :alt="c.name + ' logo'" :size="44" class="mb-1" />

            <div class="cx-name">{{ c.name }}</div>
            <div class="cx-desc text-medium-emphasis">{{ c.desc }}</div>
            <div class="cx-tag text-disabled">{{ c.tag }}</div>
          </v-card>
        </div>
      </div>

      <div class="carousel-dots">
        <button
          v-for="i in pages"
          :key="i"
          type="button"
          :class="{ active: i - 1 === page }"
          :aria-label="'Go to page ' + i"
          @click="goToPage(i - 1)"
        ></button>
      </div>
    </div>

    <p class="shot-cap text-disabled">
      <v-icon icon="mdi-shield-check-outline" size="15" class="shot-cap__icon" /> {{ caption }}
    </p>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import LogoTile from './LogoTile.vue'
import { connectors, connectorsCaption, Connector } from './landingContent'

const TRACK_GAP = 16
const TWEEN_MS = 320

@Options({
  components: { LogoTile }
})
export default class ConnectorCarousel extends Vue {
  connectors: Connector[] = connectors
  caption = connectorsCaption

  pages = 1
  page = 0
  atStart = true
  atEnd = false

  private tweenTimer: number | null = null
  private resizeTimer: number | null = null
  // assigned in mounted(); declared without initializer so vue-class-component
  // does not collect it as data (a field arrow would capture the wrong `this`)
  private resizeHandler!: () => void

  get connectorCount(): number {
    return this.connectors.filter((c) => !c.byo).length
  }

  mounted() {
    this.measure()
    this.resizeHandler = () => {
      if (this.resizeTimer != null) window.clearTimeout(this.resizeTimer)
      this.resizeTimer = window.setTimeout(() => this.measure(), 150)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  beforeUnmount() {
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler)
    if (this.tweenTimer != null) window.clearInterval(this.tweenTimer)
    if (this.resizeTimer != null) window.clearTimeout(this.resizeTimer)
  }

  private viewportEl(): HTMLElement | null {
    return (this.$refs.viewport as HTMLElement) || null
  }

  private trackEl(): HTMLElement | null {
    return (this.$refs.track as HTMLElement) || null
  }

  private cardStep(): number {
    const vp = this.viewportEl()
    const track = this.trackEl()
    const card = track?.querySelector<HTMLElement>('.cx-card')
    if (!card || !vp) return vp?.clientWidth || 1
    return card.offsetWidth + TRACK_GAP
  }

  private pageW(): number {
    const vp = this.viewportEl()
    if (!vp) return 1
    const step = this.cardStep()
    const per = Math.max(1, Math.floor((vp.clientWidth + 1) / step))
    return step * per
  }

  private curPage(): number {
    const vp = this.viewportEl()
    if (!vp) return 0
    return Math.round(vp.scrollLeft / this.pageW())
  }

  measure() {
    const vp = this.viewportEl()
    const track = this.trackEl()
    if (!vp || !track) return
    this.pages = Math.max(1, Math.ceil((track.scrollWidth - 2) / this.pageW()))
    this.update()
  }

  update() {
    const vp = this.viewportEl()
    const track = this.trackEl()
    if (!vp || !track) return
    this.page = this.curPage()
    this.atStart = vp.scrollLeft <= 2
    this.atEnd = vp.scrollLeft >= track.scrollWidth - vp.clientWidth - 2
  }

  onScroll() {
    if (this.tweenTimer == null) this.update()
  }

  private animateTo(target: number) {
    const vp = this.viewportEl()
    const track = this.trackEl()
    if (!vp || !track) return
    const max = track.scrollWidth - vp.clientWidth
    const clamped = Math.max(0, Math.min(target, max))
    const start = vp.scrollLeft
    const dist = clamped - start
    if (Math.abs(dist) < 1) {
      vp.scrollLeft = clamped
      this.update()
      return
    }
    if (this.tweenTimer != null) window.clearInterval(this.tweenTimer)
    const t0 = Date.now()
    this.tweenTimer = window.setInterval(() => {
      const p = Math.min(1, (Date.now() - t0) / TWEEN_MS)
      const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2 // easeInOutQuad
      vp.scrollLeft = start + dist * e
      if (p >= 1 && this.tweenTimer != null) {
        window.clearInterval(this.tweenTimer)
        this.tweenTimer = null
      }
      this.update()
    }, 16)
  }

  goToPage(idx: number) {
    this.animateTo(idx * this.pageW())
  }

  prevPage() {
    this.animateTo((this.curPage() - 1) * this.pageW())
  }

  nextPage() {
    this.animateTo((this.curPage() + 1) * this.pageW())
  }
}
</script>

<style scoped lang="scss">
.carousel {
  margin-top: 40px;
}
.carousel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.carousel-count {
  font-family: var(--l-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.carousel-nav {
  display: flex;
  gap: 8px;
}
.carousel-btn {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 9999px;
  border: 1px solid var(--l-border);
  color: rgb(var(--v-theme-on-background));
  transition: border-color 0.15s;
}
.carousel-btn:hover {
  border-color: rgb(var(--v-theme-primary));
}
.carousel-btn:disabled {
  opacity: 0.32;
}
.carousel-viewport {
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  border-radius: 12px;
}
.carousel-viewport::-webkit-scrollbar {
  display: none;
}
.carousel-viewport:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 3px;
}
.carousel-track {
  display: flex;
  gap: 16px;
  padding: 2px;
}
.cx-card {
  flex: 0 0 calc((100% - 32px) / 3);
  box-sizing: border-box;
  min-height: 156px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  border: 1px solid var(--l-border);
}
.cx-card--byo {
  border: 1px dashed rgb(var(--v-theme-primary));
  background: transparent;
}
.cx-logo--byo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
}
.cx-name {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
}
.cx-desc {
  font-size: 13px;
  line-height: 1.45;
  flex-grow: 1;
}
.cx-tag {
  font-family: var(--l-mono);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.carousel-dots {
  display: flex;
  gap: 7px;
  justify-content: center;
  margin-top: 22px;
}
.carousel-dots button {
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  border: none;
  background: var(--l-border);
  cursor: pointer;
  padding: 0;
  transition: background 0.15s, width 0.15s;
}
.carousel-dots button.active {
  background: rgb(var(--v-theme-primary));
  width: 22px;
}
.shot-cap {
  font-family: var(--l-mono);
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 16px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.shot-cap__icon {
  color: rgb(var(--v-theme-accent-ink));
}
@media (max-width: 900px) {
  .cx-card {
    flex-basis: calc((100% - 16px) / 2);
  }
}
@media (max-width: 560px) {
  .cx-card {
    flex-basis: 86%;
  }
}
</style>
